/**
 * Live product-list display rows for multi-furnizor products.
 * Collapsed (default): one row = implicit/default furnizor (current behaviour).
 * Expanded: one row per furnizor tab, same shape as „listă în lucru” rows.
 *
 * Display-only — does not mutate product payloads or API data.
 */

import {
    DEFAULT_PRICING_SETTINGS,
    builderFormToCalculatorInput,
    calculateProductPricing,
    mergeFormWithFurnizorPricing,
    parseNumericInput,
    resolveAppliedDiscountsDisplay,
} from '@/utils/priceListProductCalculator';
import { pricingDetailToBuilderForm } from '@/utils/pricingDetailMapper';
import {
    findMatchingLiveFurnizorTab,
    resolveFurnizorNotes,
    resolveFurnizorPricingLabel,
} from '@/utils/priceListTablePricing';

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function toNullableNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const num = parseNumericInput(value);
    return Number.isFinite(num) ? num : null;
}

/**
 * @param {Object|null|undefined} tab
 * @returns {string}
 */
export function resolveLiveFurnizorTabKey(tab) {
    return String(tab?.clientKey || tab?.id || '').trim();
}

/**
 * @param {Object|null|undefined} item
 * @returns {Object[]}
 */
export function getLiveFurnizorPricings(item) {
    const tabs = item?.pricing_detail?.furnizorPricings;
    return Array.isArray(tabs) ? tabs : [];
}

/**
 * @param {Object|null|undefined} item
 * @returns {boolean}
 */
export function isMultiFurnizorProduct(item) {
    return getLiveFurnizorPricings(item).length > 1;
}

/**
 * @param {Object|null|undefined} item
 * @returns {Object|null}
 */
export function resolveLiveDefaultFurnizorTab(item) {
    const tabs = getLiveFurnizorPricings(item);
    if (!tabs.length) {
        return null;
    }
    const defaultPricingId = item?.pricing_detail?.defaultFurnizorPricingId;
    return tabs.find((tab) => Boolean(tab?.isDefault))
        || (
            defaultPricingId != null
                ? tabs.find((tab) => (
                    tab?.id != null
                    && String(tab.id) === String(defaultPricingId)
                ))
                : null
        )
        || tabs[0]
        || null;
}

/**
 * Underlying product for edit/delete/toggles (display rows may wrap `_source`).
 * @param {Object|null|undefined} row
 * @returns {Object|null|undefined}
 */
export function resolveSourceProductItem(row) {
    return row?._source || row;
}

/**
 * @param {Object} item
 * @param {Object} furnizorTab
 * @param {Object} [pricingSettings]
 * @returns {Object}
 */
function buildLiveFurnizorChildDisplayRow(item, furnizorTab, pricingSettings = DEFAULT_PRICING_SETTINGS) {
    const detail = item?.pricing_detail || {};
    const manufacturerName = String(detail.manufacturerName || '').trim();
    const tabKey = resolveLiveFurnizorTabKey(furnizorTab);
    const settings = {
        ...DEFAULT_PRICING_SETTINGS,
        ...(detail.pricingSettingsSnapshot || {}),
        ...(furnizorTab?.pricingSettingsSnapshot || {}),
        ...pricingSettings,
    };

    const { productBuilderForm, pricingSettings: formSettings } = pricingDetailToBuilderForm(
        detail,
        item,
    );
    const mergedSettings = { ...formSettings, ...settings };
    const formTabs = Array.isArray(productBuilderForm?.furnizorPricings)
        ? productBuilderForm.furnizorPricings
        : [];
    const formTab = findMatchingLiveFurnizorTab(furnizorTab, formTabs)
        || formTabs.find((row) => resolveLiveFurnizorTabKey(row) === tabKey)
        || furnizorTab;

    let computed = {};
    try {
        computed = calculateProductPricing(
            builderFormToCalculatorInput(
                mergeFormWithFurnizorPricing(productBuilderForm, formTab),
            ),
            mergedSettings,
        ) || {};
    } catch (error) {
        console.warn('Failed to compute live furnizor display pricing', error);
        computed = {};
    }

    const supplierListPrice = toNullableNumber(
        formTab?.supplierListPriceNew
            ?? furnizorTab?.supplierListPriceNew
            ?? computed.supplierListPriceNew,
    );
    const currencyRaw = String(
        formTab?.currency
            ?? furnizorTab?.currency
            ?? detail.currency
            ?? 'ron',
    ).trim().toLowerCase();

    const priceIncVat = toNullableNumber(
        furnizorTab?.listPriceRonIncVatFinal
            ?? computed.listPriceRonIncVatFinal,
    );
    const vatRate = mergedSettings.vatRate || DEFAULT_PRICING_SETTINGS.vatRate;
    const priceExVat = toNullableNumber(computed.listPriceRonExVat)
        ?? (
            priceIncVat != null && vatRate > 0
                ? toNullableNumber(priceIncVat / vatRate)
                : null
        );
    const appliedDiscounts = resolveAppliedDiscountsDisplay(furnizorTab);

    return {
        ...item,
        id: tabKey ? `${item.id}::${tabKey}` : `${item.id}::furnizor`,
        _source: item,
        _displayRow: true,
        is_default: false,
        is_multi_furnizor_child: true,
        has_multiple_furnizori: true,
        furnizorTabKey: tabKey || null,
        furnizor: resolveFurnizorPricingLabel(furnizorTab, manufacturerName),
        supplier_list_price: supplierListPrice,
        has_discounts: appliedDiscounts.hasDiscounts,
        discount_tooltip: appliedDiscounts.tooltip,
        transport_cost_rate: toNullableNumber(
            furnizorTab?.transportCostRate
                ?? computed.transportCostRate,
        ),
        currency: currencyRaw || null,
        entry_price_ron_inc_vat: toNullableNumber(
            computed.entryPriceRonExVat ?? furnizorTab?.entryPriceRonExVat,
        ),
        markup_reference: toNullableNumber(
            computed.markupReference ?? furnizorTab?.markupReference,
        ),
        markup_actual: toNullableNumber(
            computed.markupActual ?? furnizorTab?.markupActual,
        ),
        price_ex_vat: priceExVat,
        price: priceIncVat ?? item.price,
        manufacturer_product_code: String(
            formTab?.manufacturerProductCode
                ?? furnizorTab?.manufacturerProductCode
                ?? item.manufacturer_product_code
                ?? '',
        ).trim(),
        manufacturer_product_name: String(
            formTab?.manufacturerProductName
                ?? furnizorTab?.manufacturerProductName
                ?? item.manufacturer_product_name
                ?? '',
        ).trim(),
        furnizor_notes: resolveFurnizorNotes(null, furnizorTab),
    };
}

/**
 * Builds table rows for one live product. Collapsed = default furnizor only.
 *
 * @param {Object} item
 * @param {{ expanded?: boolean, pricingSettings?: Object }} [options]
 * @returns {Object[]}
 */
export function buildLiveProductDisplayRows(item, options = {}) {
    if (!item || typeof item !== 'object') {
        return [];
    }

    const tabs = getLiveFurnizorPricings(item);
    const hasMultiple = tabs.length > 1;
    const defaultTab = resolveLiveDefaultFurnizorTab(item);
    const defaultKey = resolveLiveFurnizorTabKey(defaultTab);

    // Keep a pointer to the live product in `items` so toggles (Serial nr.,
    // vizibil în ofertă) mutate the reactive source — display rows are copies.
    const primaryRow = {
        ...item,
        _source: item,
        has_multiple_furnizori: hasMultiple,
        is_default: true,
        is_multi_furnizor_child: false,
        furnizorTabKey: defaultKey || null,
        furnizor_notes: Object.prototype.hasOwnProperty.call(item || {}, 'furnizor_notes')
            ? String(item.furnizor_notes ?? '').trim()
            : resolveFurnizorNotes(item),
    };

    if (!hasMultiple || !options.expanded) {
        return [primaryRow];
    }

    const pricingSettings = options.pricingSettings || DEFAULT_PRICING_SETTINGS;
    const rows = [primaryRow];

    for (const tab of tabs) {
        const tabKey = resolveLiveFurnizorTabKey(tab);
        const isDefault = defaultTab
            ? (
                (tabKey && tabKey === defaultKey)
                || tab === defaultTab
                || (
                    tab?.id != null
                    && defaultTab?.id != null
                    && String(tab.id) === String(defaultTab.id)
                )
            )
            : Boolean(tab?.isDefault);
        if (isDefault) {
            continue;
        }
        rows.push(buildLiveFurnizorChildDisplayRow(item, tab, pricingSettings));
    }

    const lastIndex = rows.length - 1;
    if (lastIndex < 1) {
        return rows;
    }
    return rows.map((row, index) => ({
        ...row,
        multi_furnizor_group_pos: index === 0
            ? 'start'
            : (index === lastIndex ? 'end' : 'middle'),
    }));
}

/**
 * Stable id for a live display row's multi-furnizor group (source product).
 * @param {Object|null|undefined} row
 * @returns {string|null}
 */
export function resolveLiveDisplayGroupId(row) {
    const source = resolveSourceProductItem(row);
    if (source?.id == null && row?.id == null) {
        return null;
    }
    return String(source?.id ?? row.id);
}

/**
 * Compare two scalar cell values (numbers / bools / strings), empty last.
 * @param {unknown} a
 * @param {unknown} b
 * @returns {number}
 */
function compareSortCellValues(a, b) {
    const emptyA = a === null || a === undefined || a === '';
    const emptyB = b === null || b === undefined || b === '';
    if (emptyA && emptyB) {
        return 0;
    }
    if (emptyA) {
        return 1;
    }
    if (emptyB) {
        return -1;
    }
    if (typeof a === 'boolean' && typeof b === 'boolean') {
        return Number(a) - Number(b);
    }
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }
    const strA = String(a).toLocaleLowerCase();
    const strB = String(b).toLocaleLowerCase();
    const numA = Number(strA);
    const numB = Number(strB);
    if (!Number.isNaN(numA) && !Number.isNaN(numB) && strA.trim() !== '' && strB.trim() !== '') {
        return numA - numB;
    }
    return strA.localeCompare(strB, 'ro', { sensitivity: 'accent' });
}

/**
 * Keep expanded multi-furnizor child rows glued under the implicit parent when
 * v-data-table sorts. Groups compare by the source (implicit) product values.
 *
 * Note: Vuetify treats a sortRaw result of `0` as "no result" (falsy). When the
 * anchors are equal we still return 0 and rely on stable sort / within-group
 * ordering below (parent before children uses ±1).
 *
 * @param {Object} a
 * @param {Object} b
 * @param {string} key
 * @param {{ sortByProductCode?: Function }} [options]
 * @returns {number}
 */
export function compareLiveDisplayRowsByKey(a, b, key, options = {}) {
    const sourceA = resolveSourceProductItem(a) || a;
    const sourceB = resolveSourceProductItem(b) || b;
    const groupA = resolveLiveDisplayGroupId(a);
    const groupB = resolveLiveDisplayGroupId(b);

    if (groupA != null && groupB != null && groupA === groupB) {
        const childA = a?.is_multi_furnizor_child ? 1 : 0;
        const childB = b?.is_multi_furnizor_child ? 1 : 0;
        if (childA !== childB) {
            return childA - childB;
        }
        const tabCmp = String(a?.furnizorTabKey || '')
            .localeCompare(String(b?.furnizorTabKey || ''), 'ro');
        // Avoid returning 0 so Vuetify does not discard the result as falsy when
        // two children somehow share a tab key — fall through to id string cmp.
        if (tabCmp !== 0) {
            return tabCmp;
        }
        return String(a?.id ?? '').localeCompare(String(b?.id ?? ''), 'ro') || 1;
    }

    if (key === 'product_code' && typeof options.sortByProductCode === 'function') {
        const codeCmp = options.sortByProductCode(sourceA, sourceB, 1);
        if (codeCmp) {
            return codeCmp;
        }
        // Equal codes, different products — keep a deterministic order.
        return String(groupA || '').localeCompare(String(groupB || ''), 'ro') || 1;
    }

    const valueCmp = compareSortCellValues(sourceA?.[key], sourceB?.[key]);
    if (valueCmp) {
        return valueCmp;
    }
    return String(groupA || '').localeCompare(String(groupB || ''), 'ro') || 1;
}

/**
 * Sort live display rows by column key (for v-data-table-server, which does not
 * apply header.sortRaw / custom-key-sort). Keeps multi-furnizor children glued
 * under their parent regardless of asc/desc.
 *
 * @param {Object[]} rows
 * @param {string} key
 * @param {'asc'|'desc'|string} [order='asc']
 * @param {{ sortByProductCode?: Function }} [options]
 * @returns {Object[]}
 */
export function sortLiveDisplayRowsByKey(rows, key, order = 'asc', options = {}) {
    if (!Array.isArray(rows) || !key) {
        return Array.isArray(rows) ? rows : [];
    }
    const direction = order === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const groupA = resolveLiveDisplayGroupId(a);
        const groupB = resolveLiveDisplayGroupId(b);
        if (groupA != null && groupB != null && groupA === groupB) {
            // Parent before children — never reverse within a group.
            return compareLiveDisplayRowsByKey(a, b, key, options);
        }
        return direction * compareLiveDisplayRowsByKey(a, b, key, options);
    });
}

/**
 * Attach sortRaw handlers so multi-furnizor groups stay contiguous on sort.
 *
 * @param {Object[]} headers
 * @param {{ sortByProductCode?: Function }} [options]
 * @returns {Object[]}
 */
export function withMultiFurnizorGroupSort(headers, options = {}) {
    const list = Array.isArray(headers) ? headers : [];
    return list.map((header) => {
        if (!header || header.sortable === false || !header.key) {
            return header;
        }
        if (typeof header.sortRaw === 'function') {
            return header;
        }
        const key = header.key;
        return {
            ...header,
            sortRaw: (a, b) => compareLiveDisplayRowsByKey(a, b, key, options),
        };
    });
}

/**
 * Expands filtered live products into display rows (default-only unless id is expanded).
 *
 * @param {Object[]} items
 * @param {{
 *   expandedProductIds?: Set<string>|Record<string, boolean>|string[],
 *   pricingSettings?: Object,
 * }} [options]
 * @returns {Object[]}
 */
export function expandLiveProductDisplayItems(items, options = {}) {
    const list = Array.isArray(items) ? items : [];
    const rawIds = options.expandedProductIds;
    /** @type {Set<string>} */
    let expandedIds;
    if (rawIds instanceof Set) {
        expandedIds = rawIds;
    } else if (Array.isArray(rawIds)) {
        expandedIds = new Set(rawIds.map((id) => String(id)));
    } else if (rawIds && typeof rawIds === 'object') {
        expandedIds = new Set(
            Object.keys(rawIds).filter((key) => Boolean(rawIds[key])),
        );
    } else {
        expandedIds = new Set();
    }

    const pricingSettings = options.pricingSettings;
    const rows = [];
    for (const item of list) {
        const productId = item?.id;
        const expanded = productId != null
            && expandedIds.has(String(productId));
        rows.push(...buildLiveProductDisplayRows(item, {
            expanded,
            pricingSettings,
        }));
    }
    return rows;
}
